import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, AlertTriangle, Plus } from "lucide-react";

export default function AgentRegistry() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: agents, isLoading } = useQuery({
    queryKey: ["all-agents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_registry")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const updateAgentMutation = useMutation({
    mutationFn: async ({
      id,
      verification_status,
    }: {
      id: string;
      verification_status: string;
    }) => {
      const { error } = await supabase
        .from("agent_registry")
        .update({ verification_status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      toast.success("Agent status updated");
    },
    onError: () => {
      toast.error("Failed to update agent status");
    },
  });

  const createAgentMutation = useMutation({
    mutationFn: async (agentData: any) => {
      const { error } = await supabase.from("agent_registry").insert([agentData]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-agents"] });
      setIsDialogOpen(false);
      setEditingAgent(null);
      toast.success("Agent added successfully");
    },
    onError: () => {
      toast.error("Failed to add agent");
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const agentData = {
      agency_name: formData.get("agency_name"),
      contact_name: formData.get("contact_name"),
      contact_email: formData.get("contact_email"),
      phone: formData.get("phone"),
      website: formData.get("website"),
      bio: formData.get("bio"),
      verification_status: formData.get("verification_status"),
      specialties: formData.get("specialties")
        ? (formData.get("specialties") as string).split(",").map((s) => s.trim())
        : [],
    };
    createAgentMutation.mutate(agentData);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "verified":
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
      case "flagged":
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Flagged</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Agent Registry</h1>
            <p className="text-muted-foreground">
              Manage NIL agent verification and compliance tracking
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Agent
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Agent</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="agency_name">Agency Name</Label>
                    <Input id="agency_name" name="agency_name" required />
                  </div>
                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input id="contact_name" name="contact_name" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" name="phone" type="tel" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input id="website" name="website" type="url" />
                </div>
                <div>
                  <Label htmlFor="specialties">
                    Specialties (comma-separated)
                  </Label>
                  <Input
                    id="specialties"
                    name="specialties"
                    placeholder="Social Media, Endorsements, Autographs"
                  />
                </div>
                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea id="bio" name="bio" rows={3} />
                </div>
                <div>
                  <Label htmlFor="verification_status">Status</Label>
                  <Select name="verification_status" defaultValue="pending">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="flagged">Flagged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">
                  Add Agent
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Registered Agents</CardTitle>
            <CardDescription>
              Review and manage agent verification status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agency</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Athletes</TableHead>
                  <TableHead>Deal Volume</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents?.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell className="font-medium">{agent.agency_name}</TableCell>
                    <TableCell>{agent.contact_name}</TableCell>
                    <TableCell>{agent.contact_email}</TableCell>
                    <TableCell>{agent.athlete_count}</TableCell>
                    <TableCell>${agent.deal_volume?.toLocaleString()}</TableCell>
                    <TableCell>{agent.compliance_rating?.toFixed(1) || "N/A"}</TableCell>
                    <TableCell>{getStatusBadge(agent.verification_status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {agent.verification_status !== "verified" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateAgentMutation.mutate({
                                id: agent.id,
                                verification_status: "verified",
                              })
                            }
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {agent.verification_status !== "flagged" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateAgentMutation.mutate({
                                id: agent.id,
                                verification_status: "flagged",
                              })
                            }
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
